import React, { useState, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Checkbox, Chip, CircularProgress, Collapse, FormControlLabel, InputAdornment, Link, TextField, Typography } from '@mui/material';
import LocalOffer from '@mui/icons-material/LocalOffer';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Check from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Stack } from '../design-system';
import { colors } from '../design-system';
import { useAuth } from '../context/AuthContext';
import AuthLayout, { AuthPasswordField } from '../components/auth/AuthLayout';
import {
  previewPromoCode,
  redeemPromoCode,
  storePendingRegistrationPromo,
} from '../services/promoCodesService';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

export default function RegisterPageModern({ onToggleTheme }) {
  const { t } = useTranslation('auth');
  const { signUp, refreshAuthorization } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const [form, setForm] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const promoTimer = useRef(null);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const handlePromoChange = useCallback((e) => {
    const val = e.target.value.toUpperCase();
    setPromoCode(val);
    setPromoPreview(null);
    clearTimeout(promoTimer.current);
    if (!val.trim()) return;
    setPromoChecking(true);
    promoTimer.current = setTimeout(async () => {
      try {
        const result = await previewPromoCode(val);
        setPromoPreview(result);
      } catch {
        setPromoPreview(null);
      } finally {
        setPromoChecking(false);
      }
    }, 600);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.fullName.trim() || !form.email.trim()) return setError(t('errors.required'));
    if (form.password.length < 8) return setError(t('errors.passwordLength'));
    if (form.password !== form.confirmPassword) return setError(t('errors.passwordMatch'));
    if (!termsAccepted) return setError(t('errors.terms'));
    setSubmitting(true);
    try {
      const data = await signUp({ email: form.email.trim(), password: form.password, fullName: form.fullName.trim() });
      let promoStatus = null;
      if (promoCode.trim() && data?.user?.id) {
        if (data.session?.access_token) {
          try {
            await redeemPromoCode({
              code: promoCode.trim(),
              userId: data.user.id,
              userEmail: form.email.trim(),
              accessToken: data.session.access_token,
            });
            try {
              await refreshAuthorization(data.session);
              promoStatus = 'applied';
            } catch (refreshError) {
              promoStatus = t('errors.permissions', { reason: refreshError.message || t('errors.reload') });
            }
          } catch (promoError) {
            promoStatus = promoError.message || t('errors.promo');
          }
        } else {
          try {
            storePendingRegistrationPromo({
              code: promoCode.trim(),
              userId: data.user.id,
              userEmail: form.email.trim(),
            });
            promoStatus = 'pending';
          } catch (pendingPromoError) {
            promoStatus = pendingPromoError.message || t('errors.promoSave');
          }
        }
      }
      setSuccess({ hasSession: Boolean(data?.session), promoStatus });
    } catch (signUpError) {
      setError(signUpError.message || t('errors.register'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title={t('registerTitle')} subtitle={t('registerSubtitle')} onToggleTheme={onToggleTheme}>
      {success ? (
        <Stack spacing={2.5}>
          <Alert severity="success">
            {success.hasSession
              ? t('accountActive')
              : t('confirmEmail')}
          </Alert>
          {success.promoStatus === 'pending' && (
            <Alert severity="info">
              {t('promoPending')}
            </Alert>
          )}
          {success.promoStatus === 'applied' && (
            <Alert severity="success">{t('promoApplied')}</Alert>
          )}
          {success.promoStatus && !['pending', 'applied'].includes(success.promoStatus) && (
            <Alert severity="error" role="alert">{success.promoStatus}</Alert>
          )}
          <Button className="auth-submit" component={RouterLink} to={success.hasSession ? '/dashboard' : '/login'}
            variant="contained" endIcon={<ArrowForward />}>
            {t(success.hasSession ? 'enterWorkspace' : 'backToLogin')}
          </Button>
        </Stack>
      ) : (
        <Stack component="form" onSubmit={handleSubmit} spacing={2.5} noValidate>
          {error && <Alert severity="error" role="alert">{error}</Alert>}
          <TextField label={t('fullName')} value={form.fullName} onChange={update('fullName')} autoComplete="name" required fullWidth />
          <TextField label={t('email')} type="email" value={form.email} onChange={update('email')} autoComplete="email" required fullWidth />
          <AuthPasswordField label={t('password')} value={form.password} onChange={update('password')} autoComplete="new-password" required helperText={t('passwordHint')} />
          <AuthPasswordField label={t('confirmation')} value={form.confirmPassword} onChange={update('confirmPassword')} autoComplete="new-password" required />
          {isFeatureEnabled('promoCodigos') && (
            <>
          <TextField
            label={t('promoLabel')}
            value={promoCode}
            onChange={handlePromoChange}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LocalOffer fontSize="small" sx={{ color: colors.primary[400] }} /></InputAdornment>,
              endAdornment: promoChecking ? <InputAdornment position="end"><CircularProgress size={16} aria-label={t('promoChecking')} /></InputAdornment> : null,
            }}
            helperText={t('promoHint')}
            fullWidth
          />
          <Collapse in={!!promoPreview}>
            {promoPreview && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" icon={<Check />} label={t('promoFree', { duration: promoPreview.durationLabel })} sx={{ backgroundColor: colors.success[50], color: colors.success[700] }} />
                {promoPreview.discountPercent > 0 && (
                  <Chip size="small" label={t('promoDiscount', { percent: promoPreview.discountPercent })} sx={{ backgroundColor: colors.info[50], color: colors.info[700] }} />
                )}
              </Stack>
            )}
          </Collapse>
            </>
          )}
          <FormControlLabel control={<Checkbox checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} />} label={t('terms')} />
          <Button className="auth-submit" type="submit" variant="contained" size="large" fullWidth
            endIcon={<ArrowForward />} disabled={submitting} aria-busy={submitting}>
            {submitting ? t('creatingAccount') : t('registerTab')}
          </Button>
          <Typography className="auth-account-prompt" component="p">
            {t('hasAccount')}{' '}<Link className="auth-text-link" component={RouterLink} to="/login">{t('loginLink')}</Link>
          </Typography>
        </Stack>
      )}
    </AuthLayout>
  );
}
